import React, { useState, useEffect } from 'react';
import { Search, Grid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { axiosFetch } from 'services/apiService';

const API_URL = process.env.REACT_APP_API_URL || "";


interface ApplicationDto {
  id: number;
  name: string;
  hasAccess: boolean;
}
interface User {
  id: number;
  name: string;
  email: string;
  applications: ApplicationDto[];
}

interface PermissionChange {
  userId: number;
  applicationId: number;
  hasAccess: boolean;
}

interface iUserList {
  userList: User[];
  availableApplications: ApplicationDto[];
  pendingChanges: PermissionChange[];
  setPendingChanges: (pendingChanges: PermissionChange[]) => void;
}

const Users: React.FC<iUserList> = ({userList, availableApplications, pendingChanges, setPendingChanges}) => {
  const [activeTab, setActiveTab] = useState('Users');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const itemsPerPage = 10;

  const filteredUsers = userList.filter((user: User) => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const toggleAppAccess = (userId: number, applicationId: number) => {
    const user = userList.find((u: User) => u.id === userId);
    const app = user?.applications.find((a: ApplicationDto) => a.id === applicationId);
    
    if (!user || !app) return;

    const newValue = !getCheckboxValue(userId, applicationId);
    
    const existingChangeIndex = pendingChanges.findIndex(
      change => change.userId === userId && change.applicationId === applicationId
    );
    
    if (existingChangeIndex >= 0) {
      const updatedChanges = [...pendingChanges];
      if (updatedChanges[existingChangeIndex].hasAccess === app.hasAccess) {
        updatedChanges.splice(existingChangeIndex, 1);
      } else {
        updatedChanges[existingChangeIndex].hasAccess = newValue;
      }
      setPendingChanges(updatedChanges);
    } else {
      const appIndex = availableApplications.findIndex(app => app.id === applicationId);
      if (appIndex >= 0) {
        setPendingChanges([...pendingChanges, { 
          userId, 
          applicationId, 
          hasAccess: newValue 
        }]);
      }
    }
  };

  const getCheckboxValue = (userId: number, applicationId: number) => {
    const pendingChange = pendingChanges.find(
      change => change.userId === userId && change.applicationId === applicationId
    );
    
    if (pendingChange) {
      return pendingChange.hasAccess;
    }
    
    const user = userList.find((u: User) => u.id === userId);
    const app = user?.applications.find((a: ApplicationDto) => a.id === applicationId);
    return app?.hasAccess || false;
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full boxsize0">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10" style={{left: '200px'}}>
                Email
              </th>
              {availableApplications.map((app) => (
                <th key={app.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-24">
                  {app.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedUsers.map((user: User) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                  {user.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-blue-600 hover:text-blue-800 sticky bg-white z-10" style={{left: '200px'}}>
                  <a href={`mailto:${user.email}`} className="hover:underline">
                    {user.email}
                  </a>
                </td>
                {availableApplications.map((app) => {
                  const isChanged = pendingChanges.some(
                    change => change.userId === user.id && change.applicationId === app.id
                  );
                  return (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-center">
                      <div className={`inline-flex items-center justify-center w-8 h-8 rounded ${isChanged ? 'bg-orange-100' : ''}`}>
                        <input
                          type="checkbox"
                          checked={getCheckboxValue(user.id, app.id)}
                          onChange={() => toggleAppAccess(user.id, app.id)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginatedUsers.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-sm">
            {searchTerm ? 'No users found matching your search.' : 'No users available.'}
          </p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredUsers.length)} of {filteredUsers.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-md text-sm ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="text-gray-400">...</span>;
                }
                return null;
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>

  );
}

export default Users;